/**
 * @file Règles d'activation du module bons cadeaux par établissement (frontend).
 * @module common.utils.giftVoucherActivationHelper
 *
 * Source unique de la sémantique « actif par défaut » de
 * `companyDetails.modulesActivation.giftVouchers` : un champ absent (documents
 * antérieurs à son introduction) vaut activé, seule une désactivation
 * explicite (`false`) coupe le module ou la vente. Miroir exact du helper
 * backend `core/members/companies/utils/moduleActivationHelper.js` — toute
 * lecture de ces drapeaux passe par ces fonctions, jamais un `!== false` inline.
 *
 * Un `companyDetails` absent (chargement en cours) est considéré activé :
 * gater sur sa présence côté appelant quand l'état « en chargement » doit
 * être distingué (ex. pages publiques).
 */

/**
 * Indique si le module bons cadeaux est activé pour l'établissement.
 *
 * @param {Object} companyDetails - Détails de l'établissement
 * @return {boolean} True sauf désactivation explicite du module
 */
export const isGiftVoucherModuleEnabled = (companyDetails) =>
  companyDetails?.modulesActivation?.giftVouchers?.enabled !== false;

/**
 * Indique si le drapeau « vente en ligne » est activé, indépendamment de
 * l'activation du module (utile pour afficher l'état brut du réglage).
 *
 * @param {Object} companyDetails - Détails de l'établissement
 * @return {boolean} True sauf désactivation explicite de la vente en ligne
 */
export const isGiftVoucherOnlineSaleEnabled = (companyDetails) =>
  companyDetails?.modulesActivation?.giftVouchers?.onlineSaleEnabled !== false;

/**
 * Indique si la vente en ligne est réellement disponible : module activé
 * ET vente en ligne activée (module désactivé implique vente coupée).
 *
 * @param {Object} companyDetails - Détails de l'établissement
 * @return {boolean} True si les surfaces publiques de vente doivent être exposées
 */
export const isGiftVoucherOnlineSaleAvailable = (companyDetails) =>
  isGiftVoucherModuleEnabled(companyDetails) && isGiftVoucherOnlineSaleEnabled(companyDetails);

/**
 * Construit le payload de mise à jour Company pour l'activation bons cadeaux.
 *
 * Le backend remplace l'objet `modulesActivation` entier : les champs existants
 * sont préservés par spread pour qu'un réglage jamais mentionné par l'appelant
 * (ou ajouté au schéma après coup) ne soit pas effacé silencieusement.
 *
 * @param {Object} companyDetails - Détails de l'établissement (état actuel)
 * @param {Object} overrides - Champs de `giftVouchers` à surcharger
 * @return {Object} Payload à passer à updateCompanyDetails
 */
export const buildGiftVoucherActivationPayload = (companyDetails, overrides) => ({
  modulesActivation: {
    giftVouchers: {
      ...(companyDetails?.modulesActivation?.giftVouchers || {}),
      ...overrides,
    },
  },
});
